
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { ErrorMessage, Field, Form } from "vee-validate";
import * as Yup from "yup";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

import ApiService from "@/core/services/ApiService";

interface Checkout {
  cancel_url: string;
  cancel_policy: string;
  refund_url: string;
  refund_policy: string;
  contact_url: string;
  delivery_policy: string;
  recurring_url: string;
  recurring_policy: string;
}

export default defineComponent({
  name: "merchant-checkout",
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  setup() {
    onMounted(() => {
      setTimeout(async () => {
        await getCheckouts();
      }, 1000);
    });

    const route = useRoute();
    const cancelUrl = ref("");
    const cancelPolicy = ref("");
    const refundUrl = ref("");
    const refundPolicy = ref("");
    const contactUrl = ref("");
    const deliveryPolicy = ref("");
    const recurringUrl = ref("");
    const recurringPolicy = ref("");
    const checkout = ref<Checkout>({
      cancel_url: "",
      cancel_policy: "",
      refund_url: "",
      refund_policy: "",
      contact_url: "",
      delivery_policy: "",
      recurring_url: "",
      recurring_policy: "",
    });
    const submitButton = ref<HTMLButtonElement | null>(null);

    const validation = Yup.object().shape({
      cancel_url: Yup.string().required().label("Cancel Url"),
      cancel_policy: Yup.string().required().label("Cancel Policy"),
      refund_url: Yup.string().required().label("Refund Url"),
      refund_policy: Yup.string().required().label("Refund Policy"),
      contact_url: Yup.string().required().label("Delivery Url"),
      delivery_policy: Yup.string().required().label("Delivery Policy"),
      recurring_url: Yup.string().required().label("Recurring Url"),
      recurring_policy: Yup.string().required().label("Recurring Policy"),
    });

    const getMerchantId = computed(() => {
      return route.params.id;
    });

    const getCheckouts = async () => {
      try {
        const url = `merchants/accounts/${getMerchantId.value}?fields=checkoutPage`;

        const { data } = await ApiService.get(url);
        checkout.value = JSON.parse(data.data[0].checkoutPage);

        cancelUrl.value = checkout.value.cancel_url;
        cancelPolicy.value = checkout.value.cancel_policy;
        refundUrl.value = checkout.value.refund_url;
        refundPolicy.value = checkout.value.refund_policy;
        contactUrl.value = checkout.value.contact_url;
        deliveryPolicy.value = checkout.value.delivery_policy;
        recurringUrl.value = checkout.value.recurring_url;
        recurringPolicy.value = checkout.value.recurring_policy;
      } catch (err) {
        checkout.value = {
          cancel_url: "",
          cancel_policy: "",
          refund_url: "",
          refund_policy: "",
          contact_url: "",
          delivery_policy: "",
          recurring_url: "",
          recurring_policy: "",
        };
      }
    };

    const onSubmit = async () => {
      // eslint-disable-next-line
      submitButton.value!.disabled = true;
      // Activate indicator
      submitButton.value?.setAttribute("data-kt-indicator", "on");

      const checkoutPage = { ...checkout.value };

      checkoutPage.cancel_url = cancelUrl.value;
      checkoutPage.cancel_policy = cancelPolicy.value;
      checkoutPage.refund_url = refundUrl.value;
      checkoutPage.refund_policy = refundPolicy.value;
      checkoutPage.contact_url = contactUrl.value;
      checkoutPage.delivery_policy = deliveryPolicy.value;
      checkoutPage.recurring_url = recurringUrl.value;
      checkoutPage.recurring_policy = recurringPolicy.value;

      console.log(checkoutPage);

      // eslint-disable-next-line
      const payload: any = {
        checkoutPage,
      };

      try {
        const res = await ApiService.put(
          `merchants/accounts/${getMerchantId.value}`,
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully updated the checkout!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(function () {
            getCheckouts();
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButton.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButton.value!.disabled = false;
    };

    const getIsDataEmpty = computed(() => {
      if (
        (checkout.value.cancel_url === "" ||
          checkout.value.cancel_url === undefined) &&
        (checkout.value.refund_url === "" ||
          checkout.value.refund_url === undefined) &&
        (checkout.value.contact_url === "" ||
          checkout.value.contact_url === undefined) &&
        (checkout.value.recurring_url === "" ||
          checkout.value.recurring_url === undefined)
      ) {
        return true;
      }

      return false;
    });

    return {
      cancelUrl,
      cancelPolicy,
      refundUrl,
      refundPolicy,
      contactUrl,
      deliveryPolicy,
      recurringUrl,
      recurringPolicy,

      validation,
      onSubmit,
      submitButton,
      getIsDataEmpty,
    };
  },
});
